/**
 * Centralized constants for the better-ccflare application
 * All magic numbers should be defined here to improve maintainability
 */

import type { RateLimitReason } from "@better-ccflare/types";

// Time constants (all in milliseconds)
export const TIME_CONSTANTS = {
	// Base units
	SECOND: 1000,
	MINUTE: 60 * 1000,
	HOUR: 60 * 60 * 1000,
	DAY: 24 * 60 * 60 * 1000,

	// Session durations - specifically for Anthropic usage windows
	ANTHROPIC_SESSION_DURATION_DEFAULT: 5 * 60 * 60 * 1000, // 5 hours - default for Anthropic provider session tracking
	ANTHROPIC_SESSION_DURATION_FALLBACK: 1 * 60 * 60 * 1000, // 1 hour - fallback for Anthropic provider
	/**
	 * @deprecated Use ANTHROPIC_SESSION_DURATION_DEFAULT instead.
	 * This constant is kept for backward compatibility only and should not be used in new code.
	 */
	SESSION_DURATION_DEFAULT: 5 * 60 * 60 * 1000, // 5 hours - kept for backward compatibility - new code should use ANTHROPIC_SESSION_DURATION_DEFAULT

	// Timeouts
	STREAM_TIMEOUT_DEFAULT: 1000 * 60 * 1, // 1 minute
	// IDLE window for a stream read — the maximum time to wait for the NEXT
	// chunk, not a budget for the whole stream. An Anthropic-compatible
	// upstream only sends the `message_delta` frame that carries the final
	// output_tokens at the very END of the stream, and agentic traffic
	// regularly runs 90-180s, so a whole-stream budget silently discarded
	// usage for perfectly healthy streams (a 91s / 26,366-output-token
	// response was logged as "Stream read timeout while extracting usage
	// info"). 5 minutes is sized for the other silent stretch on this path:
	// a large prompt can spend minutes in prefill before the first output
	// token, with no bytes to read in the meantime. It bounds a DEAD stream,
	// not a slow one.
	// Override at runtime via CCFLARE_STREAM_READ_TIMEOUT_MS.
	STREAM_READ_TIMEOUT_MS: 5 * 60 * 1000, // 5 minutes
	/**
	 * @deprecated No longer read anywhere. The per-read timeout it used to
	 * bound (in `extractStreamingUsage`) is now the idle window above, so a
	 * single knob — STREAM_READ_TIMEOUT_MS / CCFLARE_STREAM_READ_TIMEOUT_MS —
	 * covers it. Kept only so external configs referencing the name do not
	 * break.
	 */
	STREAM_OPERATION_TIMEOUT_MS: 30000, // 30 seconds - timeout per read operation

	OAUTH_STATE_TTL: 10, // 10 minutes (stored separately as minutes)
	RETRY_DELAY_DEFAULT: 1000, // 1 second
	PROXY_REQUEST_TIMEOUT_MS: 30 * 60 * 1000, // 30 minutes — covers long agent calls

	// Cache durations
	CACHE_YEAR: 31536000, // 365 days in seconds for HTTP cache headers

	// Token expiration durations
	API_KEY_TOKEN_EXPIRY_MS: 365 * 24 * 60 * 60 * 1000, // 1 year - for API keys that don't expire
	GOOGLE_TOKEN_EXPIRY_MS: 60 * 60 * 1000, // 1 hour - Google Cloud access tokens

	// Default cooldown applied when an upstream returns 429 *without* a
	// reset hint (no `retry-after`, no rate-limit-reset header, no SSE
	// reset frame, no usage-cache window reset). Treats the cooldown
	// as a probe interval rather than a hard ban: the account is
	// excluded for a short window, then the next request re-probes.
	// Real upstream rate-limit replies ship a retry-after / reset
	// header and use the precise value from the header — those flows
	// are unaffected by this default.
	// Override at runtime via CCFLARE_DEFAULT_COOLDOWN_NO_RESET_MS.
	DEFAULT_RATE_LIMIT_NO_RESET_COOLDOWN_MS: 60 * 1000, // 60s

	// Adaptive rate-limit cooldown with exponential backoff.
	// Cooldown for streak of n consecutive 429s = BASE * 2^(n-1), capped at MAX.
	// Override at runtime via CCFLARE_RATE_LIMIT_BACKOFF_BASE_MS /
	// CCFLARE_RATE_LIMIT_BACKOFF_MAX_MS / CCFLARE_RATE_LIMIT_RESET_STABILITY_MS.
	RATE_LIMIT_BACKOFF_BASE_MS: 30 * 1000, // 30s: cooldown for the 1st 429 in a streak
	RATE_LIMIT_BACKOFF_MAX_MS: 5 * 60 * 1000, // 5min: ceiling for the exponential ramp
	RATE_LIMIT_RESET_STABILITY_MS: 5 * 60 * 1000, // 5min: healthy operation needed to reset the streak counter

	// Fixed cooldown applied when an upstream returns 529 (overloaded_error)
	// with NO retry-after / reset hint. A 529 is a transient upstream SERVER
	// state, not a signal about the account's own quota — it says nothing
	// about how much capacity the account has left. Unlike 429 cooldowns it
	// never ramps with a streak and never touches consecutive_rate_limits:
	// that counter is reserved for genuine 429 quota exhaustion.
	//
	// This is deliberately much shorter than the reset-less 429 default
	// (DEFAULT_RATE_LIMIT_NO_RESET_COOLDOWN_MS, 60s, above): that 60s cooldown
	// is UNGATED — once it expires, full concurrency returns to the account
	// immediately, so a long cooldown is the only thing limiting request rate.
	// This 10s cooldown pairs with the single-flight probe gate
	// (getRateLimitProbeAdmission in rate-limit-cooldown.ts), but what the
	// gate actually does for a suppressed request depends on whether ANY
	// other candidate is available, not on pool size:
	// - At least one other candidate is not suppressed: once the cooldown
	//   expires, the gate admits exactly one probe request for THIS account
	//   and suppresses the rest — a suppressed request does not wait for
	//   that probe, it moves on to the next candidate account in the same
	//   selection (proxy.ts's account loop), so concurrent requests spread
	//   across the pool instead of piling onto the one recovering account.
	// - EVERY candidate is suppressed (a single-account pool, or a
	//   pool-wide overload storm where every account's probe lease happens
	//   to be held): there is no other candidate to defer to, so the
	//   request runs the highest-priority candidate ungated instead of
	//   failing outright (proxy.ts's "every candidate suppressed"
	//   fallback). The gate suppresses nothing in that case; the short 10s
	//   value is the only thing bounding how often that ungated path
	//   re-hits the account during an overload storm.
	// Override at runtime via CCFLARE_OVERLOAD_COOLDOWN_MS.
	OVERLOAD_COOLDOWN_MS: 10 * 1000, // 10s

	// Cap on a 529-with-reset cooldown duration. A 529-with-reset honors
	// Anthropic's own retry-after value (min(resetTime, now + cap)), but that
	// resetTime can come from the anthropic-ratelimit-unified-reset header —
	// a quota-window reset that can be hours away (see provider.ts:368-380) —
	// rather than a short, per-request retry-after. A quota-window timestamp
	// carries no information about how long the *overload* itself lasts, so
	// this caps at OVERLOAD scale, not the 429 ramp ceiling: deliberately
	// identical to DEFAULT_RATE_LIMIT_NO_RESET_COOLDOWN_MS (60s, above) — the
	// repo's established "no usable signal" answer. A real, short retry-after
	// (≤ 60s) is still honored literally; only a multi-hour unified-reset
	// value gets capped down to this bound.
	// Override at runtime via CCFLARE_OVERLOAD_WITH_RESET_MAX_MS.
	OVERLOAD_WITH_RESET_MAX_MS: 60 * 1000, // 60s

	// Bench applied to an account after a transient upstream server error
	// (HTTP 500/502/503/504) survived its in-place retry. Like the 529
	// cooldowns this says nothing about the account's own quota, so it never
	// ramps and never touches consecutive_rate_limits.
	//
	// Longer than OVERLOAD_COOLDOWN_MS (10s) on purpose. A 529 is Anthropic
	// telling us it is momentarily out of capacity — usually seconds. The 500s
	// this bench exists for were measured differently: one organization
	// returned 500 after 36-60s of processing, four times across 20 minutes,
	// while a sibling account served the same traffic normally. Ten seconds
	// would have put every session straight back onto the broken org. 60s is
	// the repo's established "no usable signal" answer
	// (DEFAULT_RATE_LIMIT_NO_RESET_COOLDOWN_MS above) and doubles as the cap on
	// an upstream-supplied Retry-After, so a hostile or quota-shaped header
	// cannot turn a transient server error into an hours-long bench.
	// Override at runtime via CCFLARE_SERVER_ERROR_COOLDOWN_MS.
	SERVER_ERROR_COOLDOWN_MS: 60 * 1000, // 60s
} as const;

/**
 * Read a duration (ms) override from the environment, falling back to the
 * compiled-in default unless the value is a finite, positive number.
 *
 * Only a positive finite value is a usable duration. The other outcomes are
 * not weaker settings but broken state: a negative duration puts every
 * cooldown computed from it in the past, so it expires the moment it is
 * written (silently disabling the mechanism), and Infinity benches the
 * account forever — and throws RangeError where the resulting timestamp is
 * rendered via `new Date(...).toISOString()` for the audit log. Unset,
 * unparseable and 0 keep their previous meaning of "use the default".
 */
function readDurationOverrideMs(
	raw: string | undefined,
	fallback: number,
): number {
	const parsed = Number(raw);
	return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

/**
 * Compute exponential-backoff cooldown (ms) for a given streak depth.
 *   backoff = BASE * 2^(consecutiveCount - 1), capped at MAX.
 * Reads BASE/MAX from env (CCFLARE_RATE_LIMIT_BACKOFF_BASE_MS /
 * CCFLARE_RATE_LIMIT_BACKOFF_MAX_MS), falling back to TIME_CONSTANTS
 * for anything that is not a positive finite duration.
 */
export function computeRateLimitBackoffMs(consecutiveCount: number): number {
	const count = Math.max(1, consecutiveCount);
	const base = readDurationOverrideMs(
		process.env.CCFLARE_RATE_LIMIT_BACKOFF_BASE_MS,
		TIME_CONSTANTS.RATE_LIMIT_BACKOFF_BASE_MS,
	);
	const max = readDurationOverrideMs(
		process.env.CCFLARE_RATE_LIMIT_BACKOFF_MAX_MS,
		TIME_CONSTANTS.RATE_LIMIT_BACKOFF_MAX_MS,
	);
	// Guard against overflow: 2^53 is JS safe-integer limit.
	const exponent = Math.min(count - 1, 52);
	return Math.min(base * 2 ** exponent, max);
}

/**
 * Whether a 429 that names a real reset window benches the account until that
 * reset. Reads CCFLARE_RATE_LIMIT_HONOR_EXPLICIT_RESET (default true).
 *
 * `false` restores the pre-3.5.101 `min(resetTime, now + backoff)` clamp, so a
 * deployment that treats 429s as model-scoped can keep the adaptive ramp from
 * escalating into a whole-account bench. Resets that are merely our own
 * defaults are never honoured either way — see `resetIsInferred`.
 */
export function isExplicitResetHonoured(): boolean {
	const raw = process.env.CCFLARE_RATE_LIMIT_HONOR_EXPLICIT_RESET?.trim();
	if (raw === undefined || raw === "") return true;
	return !["false", "0", "off", "no"].includes(raw.toLowerCase());
}

/**
 * Read the stability-reset window (ms) for the consecutive_rate_limits counter.
 * Reads CCFLARE_RATE_LIMIT_RESET_STABILITY_MS from env.
 */
export function getRateLimitResetStabilityMs(): number {
	return readDurationOverrideMs(
		process.env.CCFLARE_RATE_LIMIT_RESET_STABILITY_MS,
		TIME_CONSTANTS.RATE_LIMIT_RESET_STABILITY_MS,
	);
}

/**
 * Read the IDLE window (ms) for reading a streaming body: the maximum time to
 * wait for the next chunk before treating the stream as dead. It is not a
 * budget for the whole stream — a healthy agentic stream can run for minutes.
 * Reads CCFLARE_STREAM_READ_TIMEOUT_MS from env.
 */
export function getStreamReadTimeoutMs(): number {
	return readDurationOverrideMs(
		process.env.CCFLARE_STREAM_READ_TIMEOUT_MS,
		TIME_CONSTANTS.STREAM_READ_TIMEOUT_MS,
	);
}

/**
 * Read the fixed 529-overload cooldown (ms).
 * Reads CCFLARE_OVERLOAD_COOLDOWN_MS from env.
 */
export function computeOverloadCooldownMs(): number {
	return readDurationOverrideMs(
		process.env.CCFLARE_OVERLOAD_COOLDOWN_MS,
		TIME_CONSTANTS.OVERLOAD_COOLDOWN_MS,
	);
}

/**
 * Read the cap (ms) on a 529-with-reset cooldown duration.
 * Reads CCFLARE_OVERLOAD_WITH_RESET_MAX_MS from env.
 *
 * This value is applied as `min(resetTime, now + cap)`, so it is the only
 * clamp standing between a 529 that carries an anthropic-ratelimit-unified-reset
 * header (a quota window hours out) and an hours-long bench — a non-finite
 * override would not widen the cap but remove it.
 */
export function computeOverloadWithResetCapMs(): number {
	return readDurationOverrideMs(
		process.env.CCFLARE_OVERLOAD_WITH_RESET_MAX_MS,
		TIME_CONSTANTS.OVERLOAD_WITH_RESET_MAX_MS,
	);
}

/**
 * True for RateLimitReason values that represent an Anthropic 529
 * (overloaded_error) — a transient upstream state, not account quota
 * exhaustion. Used to route cooldown handling to the fixed overload
 * cooldown instead of the exponential 429 backoff ramp.
 */
export function isOverloadReason(reason: RateLimitReason): boolean {
	return (
		reason === "upstream_529_overloaded_with_reset" ||
		reason === "upstream_529_overloaded_no_reset"
	);
}

/**
 * Read the cooldown (ms) applied after a transient upstream server error
 * (500/502/503/504) outlives its in-place retry.
 * Reads CCFLARE_SERVER_ERROR_COOLDOWN_MS from env.
 *
 * Doubles as the cap on an upstream `Retry-After`: the bench is
 * `min(retryAfter, now + this)`, so an hour-long (or hostile) header cannot
 * take an account out of rotation for longer than the fixed cooldown.
 */
export function computeServerErrorCooldownMs(): number {
	return readDurationOverrideMs(
		process.env.CCFLARE_SERVER_ERROR_COOLDOWN_MS,
		TIME_CONSTANTS.SERVER_ERROR_COOLDOWN_MS,
	);
}

/**
 * Kill switch for the whole transient-5xx path (in-place retry, bench and
 * failover). `CCFLARE_SERVER_ERROR_RETRY_ENABLED=false` restores the previous
 * behaviour of forwarding a 500/502/503/504 straight to the client.
 *
 * The attempt count and backoff are deliberately NOT separate knobs — they
 * reuse CCFLARE_OVERLOAD_RETRY_* (see getOverloadRetryConfig below), because
 * both paths are "the upstream is transiently unwell, re-issue the same
 * request once" and splitting them would let the two drift apart.
 */
export function getServerErrorRetryEnabled(): boolean {
	return process.env.CCFLARE_SERVER_ERROR_RETRY_ENABLED !== "false";
}

/**
 * True for the RateLimitReason that represents a transient upstream server
 * error (HTTP 500/502/503/504). Kept separate from `isOverloadReason` because
 * the two get different cooldown durations; callers that care about "upstream
 * is transiently unwell, the account's own quota is fine" — the frozen streak,
 * the forward guard, the single-flight recovery probe — must test for both.
 */
export function isServerErrorReason(reason: RateLimitReason): boolean {
	return reason === "upstream_5xx_server_error";
}

const OVERLOAD_RETRY_MAX_ATTEMPTS_DEFAULT = 2;
const OVERLOAD_RETRY_MAX_ATTEMPTS_CAP = 10;

/**
 * Parse CCFLARE_OVERLOAD_RETRY_MAX_ATTEMPTS into a finite integer in
 * [1, OVERLOAD_RETRY_MAX_ATTEMPTS_CAP].
 *
 * The retry loops in the proxy spin on `attempt < maxAttempts` while the
 * upstream keeps returning 529/5xx, so an unclamped value is not a tuning
 * knob: "Infinity" parses truthy and retries forever against a sick upstream,
 * and a fractional value gives a surprising count. 0 and negatives are not a
 * way to disable the retry either — they would silently skip it, while the
 * documented kill switch is CCFLARE_OVERLOAD_RETRY_ENABLED=false. Anything
 * outside the range falls back to the default rather than being honored.
 */
function readRetryMaxAttempts(raw: string | undefined): number {
	if (raw === undefined || raw.trim() === "")
		return OVERLOAD_RETRY_MAX_ATTEMPTS_DEFAULT;
	const parsed = Number(raw);
	if (!Number.isFinite(parsed)) return OVERLOAD_RETRY_MAX_ATTEMPTS_DEFAULT;
	const truncated = Math.trunc(parsed);
	if (truncated < 1) return OVERLOAD_RETRY_MAX_ATTEMPTS_DEFAULT;
	return Math.min(truncated, OVERLOAD_RETRY_MAX_ATTEMPTS_CAP);
}

/**
 * Configuration for in-place retry of reset-less 529 (overloaded_error) responses.
 * Used by proxyWithAccount before applying account cooldown.
 *
 * Env knobs:
 *   CCFLARE_OVERLOAD_RETRY_ENABLED      — set to "false" to disable (default: true)
 *   CCFLARE_OVERLOAD_RETRY_MAX_ATTEMPTS — max in-place attempts, integer 1-10 (default: 2)
 *   CCFLARE_OVERLOAD_RETRY_BASE_MS      — jitter backoff base delay ms (default: 750)
 *   CCFLARE_OVERLOAD_RETRY_MAX_MS       — jitter backoff ceiling ms (default: 3000)
 */
export function getOverloadRetryConfig(): {
	enabled: boolean;
	maxAttempts: number;
	baseMs: number;
	maxMs: number;
} {
	const enabled = process.env.CCFLARE_OVERLOAD_RETRY_ENABLED !== "false";
	const maxAttempts = readRetryMaxAttempts(
		process.env.CCFLARE_OVERLOAD_RETRY_MAX_ATTEMPTS,
	);
	// baseMs/maxMs: 0 is valid (zero delay for tests), so use explicit finite check.
	const rawBase = Number(process.env.CCFLARE_OVERLOAD_RETRY_BASE_MS);
	const rawMax = Number(process.env.CCFLARE_OVERLOAD_RETRY_MAX_MS);
	const baseMs = Number.isFinite(rawBase) && rawBase >= 0 ? rawBase : 750;
	const maxMs = Number.isFinite(rawMax) && rawMax >= 0 ? rawMax : 3000;
	return { enabled, maxAttempts, baseMs, maxMs };
}

// Buffer sizes (in bytes unless specified)
export const BUFFER_SIZES = {
	// Stream usage buffer size in KB (multiplied by 1024 to get bytes)
	STREAM_USAGE_BUFFER_KB: 64,
	STREAM_USAGE_BUFFER_BYTES: 64 * 1024,

	// Stream body max size
	STREAM_BODY_MAX_KB: 256,
	STREAM_BODY_MAX_BYTES: 256 * 1024, // 256KB default

	// Anthropic provider stream cap
	ANTHROPIC_STREAM_CAP_BYTES: 32768, // 32KB

	// Stream tee default max bytes
	STREAM_TEE_MAX_BYTES: 1024 * 1024, // 1MB

	// Log file size
	LOG_FILE_MAX_SIZE: 10 * 1024 * 1024, // 10MB
} as const;

// Network constants
export const NETWORK = {
	// Ports
	DEFAULT_PORT: 8080,

	// Timeouts
	IDLE_TIMEOUT_MAX: 255, // Max allowed by Bun
} as const;

// Cache control headers
export const CACHE = {
	// HTTP cache control max-age values (in seconds)
	STATIC_ASSETS_MAX_AGE: 31536000, // 1 year
	CACHE_CONTROL_IMMUTABLE: "public, max-age=31536000, immutable",
	CACHE_CONTROL_STATIC: "public, max-age=31536000",
	CACHE_CONTROL_NO_CACHE: "no-cache, no-store, must-revalidate",
} as const;

// Request/Response limits
export const LIMITS = {
	// Request history limits
	REQUEST_HISTORY_DEFAULT: 50,
	REQUEST_DETAILS_DEFAULT: 100,
	REQUEST_HISTORY_MAX: 1000,
	LOG_READ_DEFAULT: 1000,

	// Account name constraints
	ACCOUNT_NAME_MIN_LENGTH: 1,
	ACCOUNT_NAME_MAX_LENGTH: 100,

	// UI formatting
	CONSOLE_SEPARATOR_LENGTH: 100,
	CONSOLE_COLUMN_PADDING: {
		NAME: 20,
		TYPE: 10,
		REQUESTS: 12,
		TOKEN: 10,
		STATUS: 20,
	},
} as const;

// HTTP status codes
export const HTTP_STATUS = {
	OK: 200,
	NOT_FOUND: 404,
	TOO_MANY_REQUESTS: 429,
	INTERNAL_SERVER_ERROR: 500,
	SERVICE_UNAVAILABLE: 503,
} as const;

// Account tiers - removed unused ACCOUNT_TIERS export
// Statistical calculations - removed unused STATS export
